import { T } from '@/ui/Text/Text';
import { Flex } from '@/ui/Flex/Flex';
import React from 'react';
import { useIntl } from 'react-intl';
import { IconButtonPrimary, IconChannelSelective, IconLanguage } from '@/icons';
import HorizontalDivider from '@/ui/HorizontalDivider';
import CustomSelect, { MultiSelect } from '@/ui/Select/Select';
import { useNodes } from '@xyflow/react';
import { SensorButtonNodeType } from '../../Nodes/types';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { GeneralSettingsType, LanguageOptions } from '../types';
import { generateButtonOptions } from '../../util/generateSettingsOptions';
import { findLanguageAnyNodes } from '../../util/findLanguageAnyNodes';

function ButtonSettings({
  settings,
  selectedNode,
  updateNodeData,
  selectedButtons,
  setSelectedSensors,
  disabled,
}: {
  settings: GeneralSettingsType;
  selectedNode: SensorButtonNodeType;
  updateNodeData: (id: string, newData: any) => void;
  selectedButtons: string[];
  setSelectedSensors: React.Dispatch<
    React.SetStateAction<{
      buttons: string[];
      buzzers: string[];
      trihows: string[];
    }>
  >;
  disabled?: boolean;
}) {
  const intl = useIntl();

  // get all buttons that have "Any" as language to filter the options accordingly
  const nodes = useNodes();
  const { languageAnyButtons } = findLanguageAnyNodes(nodes);

  const faqs = [
    ...(settings.languageSelective
      ? [
          {
            question: intl.formatMessage({
              defaultMessage: 'What is the purpose of Language in this block?',
              id: 'Yg3yKs',
            }),
            answer: intl.formatMessage({
              defaultMessage:
                'Language defines under which language the logic for this specific button should work. If it is set to "Any", the logic for this specific button will work on anylanguage.',
              id: 'z4VpP6',
            }),
          },
        ]
      : []),
  ];
  const relevantData = selectedNode.data.options;

  // generate button options based on already selected buttons
  const buttonOptions = generateButtonOptions(
    settings.languageSelective,
    settings.buttons,
    selectedButtons,
    relevantData.buttons ?? [],
    languageAnyButtons,
  );

  return (
    <StyledSettings data-testid="sensor-button-settings">
      <Flex align="center" gap={8}>
        <IconButtonPrimary />
        <Flex direction="column">
          <T h3>
            {intl.formatMessage({ defaultMessage: 'Button', id: 'KP63fg' })}
          </T>
        </Flex>
      </Flex>
      <StyledSettingsContent>
        <Flex direction="column" gap={8}>
          <MultiSelect
            disabled={disabled ?? false}
            required
            title={intl.formatMessage({
              defaultMessage: 'Button',
              id: 'KP63fg',
            })}
            placeholder={intl.formatMessage({
              defaultMessage: 'Select Button(s)',
              id: '7d/miY',
            })}
            icon={<IconChannelSelective />}
            options={buttonOptions}
            size="small"
            onChange={(e) => {
              updateNodeData(selectedNode.id, {
                ...selectedNode.data,
                options: {
                  ...relevantData,
                  buttons: e
                    ? e.map((item) => ({
                        triggerId: item.value,
                        name: item.label,
                      }))
                    : null,
                },
              });
              setSelectedSensors((prev) => {
                // Get previous buttons selected by this node
                const prevNodeButtons = Array.isArray(relevantData.buttons)
                  ? relevantData.buttons.map((b) => b.triggerId)
                  : [];

                // Get new buttons selected by this node
                const newNodeButtons: string[] = Array.isArray(e)
                  ? e.map((entry) => entry.value)
                  : [];

                // Remove buttons previously selected by this node
                const withoutPrevNodeButtons = prev.buttons.filter(
                  (button) => !prevNodeButtons.includes(button),
                );
                // Add new buttons selected by this node
                const updatedButtons = [
                  ...withoutPrevNodeButtons,
                  ...newNodeButtons,
                ];
                // Remove duplicates
                const uniqueButtons = Array.from(new Set(updatedButtons));
                return {
                  ...prev,
                  buttons: uniqueButtons,
                };
              });
            }}
            value={
              relevantData.buttons && relevantData.buttons.length > 0
                ? relevantData.buttons.map((t) => ({
                    label: t.name,
                    value: t.triggerId,
                  }))
                : null
            }
          />
          {/* eslint-disable-next-line no-nested-ternary */}
          {relevantData.buttons && relevantData.buttons.length === 1 ? (
            <T body2>
              {intl.formatMessage({
                defaultMessage:
                  'To assign a slide to this button, enter the following ID in the CMS: ',
                id: 'UcMzif',
              })}
              {`${relevantData.buttons[0].triggerId}`}
            </T>
          ) : relevantData.buttons && relevantData.buttons.length > 1 ? (
            <T body2>
              {intl.formatMessage({
                defaultMessage:
                  'To assign a slide to multiple Buttons, you need to define a custom generic key and assign it to the slide.',
                id: 'RBDJOO',
              })}
            </T>
          ) : null}
          {settings.languageSelective && (
            <Flex justify="space-between" align="center">
              <T body1>
                {intl.formatMessage({
                  defaultMessage: 'Works in Language',
                  id: 'M+Dbq2',
                })}
              </T>
              <CustomSelect
                disabled={disabled ?? false}
                size="small"
                options={LanguageOptions.map((lan) => ({
                  value: lan.value,
                  label: lan.value
                    ? lan.value
                    : intl.formatMessage({
                        defaultMessage: 'Any',
                        id: 'ToO3/h',
                      }),
                }))}
                value={
                  relevantData.language
                    ? {
                        label: relevantData.language,
                        value: relevantData.language,
                      }
                    : {
                        label: intl.formatMessage({
                          defaultMessage: 'Any',
                          id: 'ToO3/h',
                        }),
                        value: null,
                      }
                }
                onChange={(e) => {
                  updateNodeData(selectedNode.id, {
                    ...selectedNode.data,
                    options: { ...relevantData, language: e?.value },
                  });
                }}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Language',
                  id: 'y1Z3or',
                })}
                isSearchable={false}
                icon={<IconLanguage />}
              />
            </Flex>
          )}
        </Flex>
        <HorizontalDivider />
      </StyledSettingsContent>
      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}

export default ButtonSettings;
